/**
 * Generic WebSocket event hook factory
 * Consolidates common patterns: auth check, subscribe/unsubscribe, optional batching
 *
 * @param {Object} config
 * @param {string} config.eventType - WebSocket event name to subscribe to
 * @param {Function} config.handler - Event handler function (receives event data)
 * @param {Function} config.validate - Optional validation function (returns true if event is valid)
 * @param {boolean} config.isAuthenticated - Whether user is authenticated
 * @param {Object} config.dependencies - React dependencies array for useEffect
 * @param {Object} config.batch - Optional batching config { windowMs, processEvents }
 */

import { useEffect, useRef, useCallback } from 'react'
import websocketService from '../services/websocket'

export function useWebSocketEvent({
  eventType,
  handler,
  validate,
  isAuthenticated = false,
  dependencies = [],
  batch = null,
}) {
  const batchRef = useRef({ events: [], timeoutId: null })

  // Memoize the handler to prevent unnecessary re-subscriptions
  const stableHandler = useCallback(handler, dependencies)
  const stableValidate = useCallback(validate || (() => true), [validate])

  useEffect(() => {
    // Only set up listener if authenticated
    if (!isAuthenticated) {
      return
    }

    const eventHandler = (event) => {
      // Validate event data
      if (!stableValidate(event)) {
        console.warn(`${eventType} event failed validation:`, event)
        return
      }

      // If batching is enabled, collect events
      if (batch) {
        batchRef.current.events.push(event)

        // Clear existing timeout
        if (batchRef.current.timeoutId) {
          clearTimeout(batchRef.current.timeoutId)
        }

        // Set timeout to process batched events
        batchRef.current.timeoutId = setTimeout(() => {
          const events = batchRef.current.events
          batchRef.current.events = []
          batchRef.current.timeoutId = null

          if (events.length > 0) {
            batch.processEvents(events)
          }
        }, batch.windowMs)
      } else {
        // No batching - call handler immediately
        stableHandler(event)
      }
    }

    // Subscribe to event
    websocketService.on(eventType, eventHandler)

    // Cleanup on unmount
    return () => {
      websocketService.off(eventType, eventHandler)
      if (batchRef.current.timeoutId) {
        clearTimeout(batchRef.current.timeoutId)
      }
    }
  }, [eventType, stableHandler, stableValidate, isAuthenticated, batch])
}

/**
 * Hook for simple WebSocket events (no batching)
 * Convenience wrapper with simplified API
 */
export function useSimpleWebSocketEvent(eventType, handler, isAuthenticated, deps = []) {
  useWebSocketEvent({
    eventType,
    handler,
    isAuthenticated,
    dependencies: deps,
  })
}

/**
 * Hook for batched WebSocket events
 * Collects events over a time window before processing
 */
export function useBatchedWebSocketEvent(eventType, processBatch, validate, isAuthenticated, windowMs = 500, deps = []) {
  useWebSocketEvent({
    eventType,
    handler: () => {}, // Not used when batching
    validate,
    isAuthenticated,
    dependencies: deps,
    batch: {
      windowMs,
      processEvents: processBatch,
    },
  })
}

export default useWebSocketEvent
